/**
 * useTravelListAI — ArcheAICapabilities for the Travel list/landing page.
 *
 * Same feature-flag semantics as useTravelAI, but the state snapshot reflects
 * the list-level view (no single active trip). Both hooks delegate to
 * `buildTravelAIActions` so they stay in sync as features are added.
 */

import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ArcheAICapabilities } from '@/components/fias-ai/types';
import { travelApi } from '../api';
import type { TravelerProfileFields, TravelUserProfileResponse } from '../api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import { useTravelUserProfile } from './useTravelUserProfile';
import { buildTravelAIActions } from './useTravelAIActions';
import { TRAVEL_ARCHE_ID } from './useTravelAI';

// The list view has no active trip, so trip-scoped actions (add_day,
// update_event, geocode_*, etc.) have nothing to target. Exposing them in
// the catalog without a matching applyAction case meant the AI would
// happily emit tool calls that silently dropped on the floor — see the
// create_trip-from-list bug. Scope the catalog to the actions the list view
// can actually execute.
const LIST_VIEW_ACTION_IDS = new Set(['update_profile', 'toggle_feature', 'create_trip']);

type CreateTripParams = Parameters<typeof travelApi.createTravelTrip>[0];

export function useTravelListAI(): ArcheAICapabilities {
  const { profile, features } = useTravelUserProfile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const getStateSnapshot = useCallback((): Record<string, unknown> => {
    const profileEnabled = features.traveler_profile !== false;
    return {
      view: 'trip-list',
      features,
      ...(profileEnabled ? { profile } : {}),
    };
  }, [profile, features]);

  const applyAction = useCallback(
    (actionId: string, params: Record<string, unknown>) => {
      switch (actionId) {
        case 'update_profile': {
          const patch = params as Partial<TravelerProfileFields>;
          void travelApi.updateTravelUserProfile(patch).then((res) => {
            queryClient.setQueryData<TravelUserProfileResponse | undefined>(
              queryKeys.travel.profile(),
              (prev) =>
                prev
                  ? { ...prev, profile: res.profile, updatedAt: res.updatedAt }
                  : { profile: res.profile, features: {}, updatedAt: res.updatedAt },
            );
          });
          break;
        }
        case 'toggle_feature': {
          const key = params.key as string | undefined;
          const enabled = params.enabled as boolean | undefined;
          if (!key || typeof enabled !== 'boolean') return;
          void travelApi.setTravelFeature(key, enabled).then((res) => {
            queryClient.setQueryData<TravelUserProfileResponse | undefined>(
              queryKeys.travel.profile(),
              (prev) => (prev ? { ...prev, features: res.features } : prev),
            );
          });
          break;
        }
        case 'create_trip': {
          const payload: CreateTripParams = {
            title: params.title as string | undefined,
            startDate: params.startDate as string | undefined,
            initialDays: params.initialDays as CreateTripParams['initialDays'],
            initialTransits: params.initialTransits as CreateTripParams['initialTransits'],
          };
          void travelApi
            .createTravelTrip(payload)
            .then((res) => {
              // Seed the detail-query cache from the server's full response so
              // the planner page renders the populated trip immediately on
              // navigation — without this it would mount with no data and the
              // user has to wait for (or refresh to trigger) the next GET.
              if (res.trip?.tripId) {
                queryClient.setQueryData(queryKeys.travel.detail(res.trip.tripId), {
                  trip: res.trip,
                  items: res.items ?? [],
                  events: res.events ?? [],
                  attachments: res.attachments ?? [],
                });
              }
              void queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
              // Tell the user when the server couldn't add some of what the AI
              // asked for (bad enum, blank title, etc.) rather than silently
              // landing them in a trip that's missing items.
              if (res.skippedCommands && res.skippedCommands.length > 0) {
                toast({
                  description: `Trip created — but ${res.skippedCommands.length} item(s) couldn't be added and were skipped.`,
                });
              }
              // When the AI builds out a real itinerary in one shot, navigate
              // into the trip so the user can see what was created.
              const hasContent =
                (payload.initialDays?.length ?? 0) > 0 ||
                (payload.initialTransits?.length ?? 0) > 0;
              if (hasContent && res.trip?.tripId) {
                navigate(`/a/arche_travel/${res.trip.tripId}`);
              }
            })
            .catch(() => {
              toast({
                description: "Sorry — I couldn't create that trip. Please try again.",
              });
            });
          break;
        }
      }
    },
    [queryClient, navigate],
  );

  return useMemo(
    () => ({
      archeId: TRAVEL_ARCHE_ID,
      enabled: true,
      // Single shared session across the whole travel arche so a "plan me a
      // trip" conversation on the list page still shows up after we navigate
      // into the new trip. See chatSessionCache.ts.
      sessionKey: 'arche_travel',
      archeDescription:
        'Travel Planner arche — list view. Users can create new trips, manage their persistent ' +
        'traveler profile, and toggle feature flags. To edit days, events, expenses, etc., the ' +
        'user must open a specific trip first.',
      actions: buildTravelAIActions(features).filter((a) => LIST_VIEW_ACTION_IDS.has(a.id)),
      getStateSnapshot,
      applyAction,
    }),
    [features, getStateSnapshot, applyAction],
  );
}
